import React, {
  useMemo,
  useCallback,
  useEffect,
  useState,
  useRef,
  useImperativeHandle,
  ForwardedRef,
} from 'react'
import classNames from 'classnames'

import { useResizeObserver } from '@/hooks'

interface IAccordionControl {
  toggle: () => void
}

interface Props {
  accordionRef?: ForwardedRef<HTMLDivElement>
  disabled?: boolean
  expanded?: boolean
  preExpanded?: boolean
  title: string
  width?: string | number
  isFormElement?: boolean
  // eslint-disable-next-line no-unused-vars
  onToggle?: (expanded: boolean) => void
  // eslint-disable-next-line no-unused-vars
  children: (props: any) => React.ReactNode
  [x: string]: any
}

const Accordion = React.forwardRef<IAccordionControl, Props>(
  (
    {
      accordionRef,
      disabled,
      preExpanded,
      title,
      expanded,
      width = '100%',
      onToggle,
      children,
      ...rest
    },
    controlRef,
  ) => {
    const contentRef = useRef<HTMLDivElement>(null)

    const [isInternalExpanded, setIsInternalExpanded] = useState<
      boolean | undefined
    >()

    const [contentHeight, setContentHeight] = useState<string>(
      isInternalExpanded ? 'auto' : '0',
    )

    useResizeObserver(contentRef, () => {
      setContentHeight(`${contentRef?.current?.scrollHeight || 0}px`)
    })

    useEffect(() => {
      setContentHeight(`${contentRef?.current?.scrollHeight || 0}px`)
    }, [])

    /** Set initial expand status */
    useEffect(() => {
      if (isInternalExpanded === undefined) {
        setIsInternalExpanded(preExpanded)
      }
    }, [isInternalExpanded, preExpanded])

    const isControlled = useMemo(
      () => preExpanded === undefined && expanded !== undefined,
      [expanded, preExpanded],
    )

    const isExpanded = useMemo(
      () => (isControlled ? expanded : isInternalExpanded),
      [expanded, isControlled, isInternalExpanded],
    )

    const onChange = useCallback(() => {
      if (disabled) return

      if (isControlled) {
        if (onToggle) onToggle(!isExpanded)
      } else {
        setIsInternalExpanded(!isInternalExpanded)
      }
    }, [disabled, isControlled, isExpanded, isInternalExpanded, onToggle])

    const onClose = useCallback(() => {
      if (isControlled) {
        if (onToggle) onToggle(false)
      } else {
        setIsInternalExpanded(false)
      }
    }, [isControlled, onToggle])

    const onKeyDown = useCallback(
      (ev: React.KeyboardEvent) => {
        if (disabled) return

        if (ev.key === 'Enter' || ev.key === ' ') {
          onChange()
        } else if (ev.key === 'Escape') {
          onClose()
        }
      },
      [disabled, onChange, onClose],
    )

    useImperativeHandle(
      controlRef,
      () => ({
        toggle() {
          onChange()
        },
      }),
      [onChange],
    )

    return (
      <div
        className={classNames('accordion-container', {
          isExpanded,
        })}
        ref={accordionRef}
        style={{ width: typeof width === 'number' ? `${width}px` : width }}
      >
        <div
          className={classNames('accordion-header')}
          onClick={onChange}
          onKeyDown={onKeyDown}
        >
          <span className="accordion-title">{title}</span>
          <div className="accordion-icon-container">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="--icon-tablecell-neutral-highlighted"
              xmlns="http://www.w3.org/2000/svg"
              className="icon"
            >
              <path
                // eslint-disable-next-line max-len
                d="M15.418 9.85897C15.5481 9.73012 15.7239 9.65784 15.907 9.65784C16.0901 9.65784 16.2659 9.73012 16.396 9.85897C16.4602 9.92224 16.5113 9.99766 16.5461 10.0808C16.5809 10.164 16.5989 10.2533 16.5989 10.3435C16.5989 10.4336 16.5809 10.5229 16.5461 10.6061C16.5113 10.6893 16.4602 10.7647 16.396 10.828L12.488 14.658C12.3576 14.7866 12.1817 14.8588 11.9985 14.8588C11.8153 14.8588 11.6394 14.7866 11.509 14.658L7.601 10.828C7.53675 10.7647 7.48572 10.6893 7.45089 10.6061C7.41607 10.5229 7.39813 10.4336 7.39813 10.3435C7.39813 10.2533 7.41607 10.164 7.45089 10.0808C7.48572 9.99766 7.53675 9.92224 7.601 9.85897C7.73113 9.73012 7.90686 9.65784 8.09 9.65784C8.27313 9.65784 8.44886 9.73012 8.579 9.85897L12 13L15.418 9.85897Z"
                fill="#0C0024"
              ></path>
            </svg>
          </div>
        </div>
        <div
          ref={contentRef}
          className="accordion-content"
          style={{
            maxHeight: isExpanded ? contentHeight : 0,
            height: isExpanded ? contentHeight : 0,
          }}
        >
          {children({ ...rest, isformelement: `${!!rest.isFormElement}` })}
        </div>
      </div>
    )
  },
)

export default Accordion
